import { DosingRegimenEnum, PrescriptionFragment } from '@pm/graphql';
import { addDays, compareDesc, differenceInDays, parseISO } from 'date-fns';
import { BrandId } from '../types';

export type PrescriptionOption = {
  value: string;
  length: number;
};

const defualtPrepPrescriptionOptions = [
  { value: '30', length: 30 },
  { value: '60', length: 60 },
  { value: '90', length: 90 },
];

/**
 * Gets available PrEP Rx prescription options
 *
 * @returns {PrescriptionOption[]} list of available PrEP prescription options
 */
export const getPrepPrescriptionOptions = (): PrescriptionOption[] => {
  return defualtPrepPrescriptionOptions;
};

/**
 * Gets duration of prescription type
 *
 * @param {PrescriptionOption[]} prescriptionOptions available prescription options
 * @param {string} typeValue type of prescription (i.e., 'Initial 30', 'Refill 60')
 * @returns {number} length of the given prescription in days (i.e., 30, 60, 90)
 */
export const prepPrescriptionLength = (
  prescriptionOptions: PrescriptionOption[],
  typeValue: string,
) => {
  return prescriptionOptions.find((obj) => {
    return obj.value === typeValue;
  })?.length;
};

/**
 * Gets number of pills remaining for a given prescription. If the due at date is provided,
 * the number of pills is the difference in days between today and the due date; otherwise,
 * the due date is estimated based on the prescription creation date and the prescription length.
 *
 * @param {PrescriptionFragment} prescription prescription to get the remaining number of pills for
 * @returns {number} number of pills left in prescription
 */
export const getNumPillsRemainingInPrescription = (
  prescription: PrescriptionFragment,
): number => {
  const endDate = prescription.dueAt
    ? parseISO(prescription.dueAt)
    : addDays(
        parseISO(prescription.createdAt),
        prescription.prescriptionLength ?? 0,
      );

  const numDaysUntilDue = differenceInDays(endDate, new Date());

  return numDaysUntilDue > 0 ? numDaysUntilDue : 0;
};

/**
 * Gets number of days until a prescription's due date. Returns a negative value if in the past.
 * Will use the prescription's `dueAt` date if available, otherwise will use the `createdAt` date.
 */
export const numberOfDaysUntilPrescriptionEnd = (
  prescription: PrescriptionFragment,
) => {
  const endDate = prescription.dueAt
    ? parseISO(prescription.dueAt)
    : addDays(
        parseISO(prescription.createdAt),
        prescription.prescriptionLength ?? 0,
      );

  const numDaysUntilDue = differenceInDays(endDate, new Date());

  return numDaysUntilDue;
};

/**
 * Gets the latest prescription, based on treatment start date.
 *
 * @param {PrescriptionFragment[]} prescriptions prescriptions to compare for the most recent
 * @returns {PrescriptionFragment} latest prescription
 */
export const getLatestPrescription = (
  prescriptions: PrescriptionFragment[],
): PrescriptionFragment | undefined => {
  const mostRecentToLeastRecentPrescriptions = prescriptions.sort(
    (prescription1, prescription2) => {
      return compareDesc(
        parseISO(prescription1.treatmentStartAt || ''),
        parseISO(prescription2.treatmentStartAt || ''),
      );
    },
  );

  return mostRecentToLeastRecentPrescriptions[0];
};

type IssuablePrescription = {
  sentAt?: string | null;
  prescribedDate?: string | null;
  createdAt?: string | null;
};

/**
 * The date a prescription was written, as an ISO string.
 *
 * Both EMR integrations record the written date, just in different columns:
 * Juno/FHIR (CA) sets `prescribedDate` from authoredOn, while Healthie (US)
 * writes date_written into `sentAt` and never sets `prescribedDate`. Reading
 * either field alone leaves the date blank in one market, so both are tried
 * before falling back to `createdAt`.
 *
 * `prescribedDate` is checked first, matching `Prescription#sent_on` on the
 * back end. The order matters for CA prescriptions sent manually
 * (Steps::SendPrescriptionToPatient sets a clinician-entered `prescribedDate`
 * *and* stamps `sentAt` with the current time): taking `sentAt` first would
 * show the day the script was transmitted rather than the day it was written,
 * and disagree with the admin views that read `sent_on`.
 *
 * @param {IssuablePrescription} prescription prescription to read the date from
 * @returns {string | null} ISO date string, or null when none of the fields are set
 */
export const getPrescriptionIssuedOn = (
  prescription: IssuablePrescription | null | undefined,
): string | null =>
  prescription?.prescribedDate ??
  prescription?.sentAt ??
  prescription?.createdAt ??
  null;

export type PastPrescription = {
  id: string;
  medication: string;
  sentAt: string | null;
  prescribedDate: string | null;
  createdAt: string;
  pharmacyName?: string | null;
  genericName?: string | null;
  pharmacyPhone?: string | null;
  drugCode?: string | null;
  /** Dispensed amount, in `unit`s. Meaningless without one. */
  quantity?: number | null;
  /** Dispense unit as the EMR recorded it — free text, e.g. "tablet", "mL". */
  unit?: string | null;
  instructions?: string | null;
};

/**
 * Maps a treatment's prescriptions to the rows its Past Prescriptions list
 * renders, newest first (the order the connection returns).
 *
 * The current prescription is excluded: it is already shown in the treatment
 * details header, so it is not a *past* prescription. Rows are identified by
 * medication name, so a prescription without one is dropped as unrenderable.
 *
 * @param {PrescriptionFragment[]} prescriptions every prescription for the treatment
 * @param {PrescriptionFragment} currentPrescription the prescription shown in the header, excluded from the result
 * @returns {PastPrescription[]} the patient's past prescriptions
 */
export const toPastPrescriptions = (
  prescriptions: PrescriptionFragment[],
  currentPrescription?: PrescriptionFragment,
): PastPrescription[] =>
  prescriptions
    .filter(
      (rx) =>
        rx.id !== currentPrescription?.id && rx.readableMedication != null,
    )
    .map((rx) => ({
      id: rx.id,
      medication: rx.readableMedication as string,
      sentAt: rx.sentAt ?? null,
      prescribedDate: rx.prescribedDate ?? null,
      createdAt: rx.createdAt,
      // The EMR sync persists the pharmacy name into `fulfillmentMethod` for
      // both CA/Juno and US/Healthie. NOTE: the filfilmentMethod is not quite the same as pharmacy.name, since it also has the city folded into it.
      pharmacyName: rx.fulfillmentMethod ?? null,
      pharmacyPhone: rx.pharmacyCatalogue?.phoneNumber ?? null,
      drugCode: rx.drugCode ?? null,
      quantity: rx.quantity ?? null,
      unit: rx.unit ?? null,
      instructions: rx.instructions ?? null,
      // TODO: no generic (molecule) name is exposed on the patient-facing
      // Prescription type yet.
      genericName: null,
    }));

/**
 * Gets list of prescriptions matching a specific brand
 *
 * @param {PrescriptionFragment[]} prescriptions prescriptions to filter from
 * @param {BrandId} brandId the brand ID to filter prescriptions with
 * @returns {PrescriptionFragment[]} prescriptions only containing the prescription matching the provided brand
 */
export const filterPrescriptionsByBrand = (
  prescriptions: PrescriptionFragment[],
  brandId: BrandId,
): PrescriptionFragment[] => {
  return prescriptions.filter(
    (prescription) => prescription.brand?.id === brandId,
  );
};

// We should use null, not a string, going forward.
const NA_PREP_TYPE = 'n/a';

const getKnownPrepType = (
  prepType: PrescriptionFragment['prepType'] | undefined,
): string | undefined =>
  prepType && prepType !== NA_PREP_TYPE ? prepType : undefined;

const getKnownMedication = (
  medication: PrescriptionFragment['medication'] | undefined,
): string | undefined => (medication ? medication : undefined);

/**
 * Gets the latest Freddie PrEP prescription from a list of prescriptions
 *
 * @param {PrescriptionFragment[]} prescriptions prescriptions to check against
 * @returns {PrescriptionFragment} latest Freddie PrEP prescription
 */
export const getLatestPrepPrescription = (
  prescriptions: PrescriptionFragment[],
): PrescriptionFragment | undefined => {
  const prepPrescriptions = prescriptions.filter((prescription) =>
    getKnownPrepType(prescription.prepType),
  );
  const latestPrepPrescription = prepPrescriptions[0];

  return latestPrepPrescription;
};

export type MedicationNameSource = {
  medication?: PrescriptionFragment['medication'];
  prepType?: PrescriptionFragment['prepType'];
  prescriptionType?: PrescriptionFragment['prescriptionType'];
};

export const getPrescriptionMedicationName = (
  prescription: MedicationNameSource,
): string | undefined =>
  getKnownMedication(prescription.medication) ??
  getKnownPrepType(prescription.prepType) ??
  prescription.prescriptionType ??
  undefined;

export const isMedicationNameFromPrescriptionType = (
  prescription: MedicationNameSource,
): boolean =>
  !getKnownMedication(prescription.medication) &&
  !getKnownPrepType(prescription.prepType) &&
  !!prescription.prescriptionType;

/**
  @returns {string[]} an array of fulfillment methods
*/
export function getFulfillmentMethods() {
  return [
    'Affirming Care (Alberta)',
    'Affirming Care (Ontario)',
    'Affirming Care (Toronto)',
    'Gilead Pharmacy (PAP Program)',
    'Meadowood (MB Neighbourly)',
    'Earls Pharmacy (SK Neighbourly)',
    'Mint Edmonton',
    'Non-Partner Pharmacy',
  ];
}

const DOSING_REGIMEN_LABELS: Record<DosingRegimenEnum, string> = {
  [DosingRegimenEnum.Daily]: 'Daily use',
  [DosingRegimenEnum.OnDemand]: 'On-demand',
  [DosingRegimenEnum.Injectable]: 'Injectable',
};

export function getDosingRegimenLabel(
  value?: DosingRegimenEnum | null,
): string {
  return value ? DOSING_REGIMEN_LABELS[value] : '';
}

const TOGGLEABLE_DOSING_REGIMENS = [
  DosingRegimenEnum.Daily,
  DosingRegimenEnum.OnDemand,
];

/**
  @returns {object[]} an array of PrEP dosing regimen options
*/
export function getDosingRegimenOptions() {
  return TOGGLEABLE_DOSING_REGIMENS.map((value) => ({
    label: DOSING_REGIMEN_LABELS[value],
    value,
  }));
}

/**
  @returns {object[]} the toggleable options, plus the prescription's own regimen when it
  is not one of them
*/
export function getDosingRegimenOptionsIncludingCurrent(
  currentRegimen?: DosingRegimenEnum | null,
) {
  const options = getDosingRegimenOptions();
  if (!currentRegimen || TOGGLEABLE_DOSING_REGIMENS.includes(currentRegimen)) {
    return options;
  }

  return [
    ...options,
    { label: DOSING_REGIMEN_LABELS[currentRegimen], value: currentRegimen },
  ];
}

/**
 * Whether a prescription id is absent from a loaded list.
 *
 * `loading` and `error` gate the answer so an id is never judged absent from a
 * list that has not arrived yet.
 */
export const isPrescriptionAbsent = ({
  prescriptions,
  prescriptionId,
  loading,
  error,
}: {
  prescriptions: Pick<PastPrescription, 'id'>[];
  prescriptionId?: string | null;
  loading: boolean;
  error: boolean;
}): boolean =>
  !loading &&
  !error &&
  prescriptionId != null &&
  !prescriptions.some(({ id }) => id === prescriptionId);
